import React, { FC, useMemo, useState } from 'react';
import { Text } from '@opendoor/bricks/core';

import { useHistory } from 'platform/HistoryProvider';
import { StepInfo, Wizard } from 'shared/components/Wizard';
import { ClosingTaskWizardLayout } from 'sellDirect/components/closingDash/ClosingTaskWizardLayout';
import { HelloSignWizardLayout } from 'sellDirect/components/closingDash/HelloSignWizardLayout';

import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { ClosedOffer, UnderContractOffer } from 'sellDirect/OfferContext';
import { getHubSellingRoute } from 'shared/helpers/routes';
import {
  HelloSignStep,
  ContractData,
} from 'sellDirect/components/closingDash/helloSign/HelloSignStep';
import { SignContractResult } from 'sellDirect/graphql/useSignContract';
import { showToast } from '@opendoor/bricks/complex/Toast';
import {
  RequestLateCheckoutStep,
  RequestLateCheckoutSummaryStep,
} from 'sellDirect/components/closingDash/lateCheckout/LateCheckoutStep';
import { HelloSignWizardStep } from 'shared/helpers/constants';
import { generatePath } from 'react-router-dom';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

export interface LateCheckoutWizardProps {
  /**  under contract offer or closed offer */
  offer: UnderContractOffer | ClosedOffer;
  /**  Current closing tasks */
  closingTasks: ClosingTasks;
}

/**
 * Wizard to allow users to request a late checkout and sign / upload the late checkout addendum
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const LateCheckoutWizard: FC<LateCheckoutWizardProps> = ({ offer, closingTasks }) => {
  const { addressUUID } = useQualificationContext();
  const { history, goBack } = useHistory();
  const initialLocation = useMemo(() => history.location, [history]);
  const [contractData, setContractData] = useState<ContractData>();
  // If the user has not yet setup the contract, we show the form
  const hasNotSetALateCheckout = !closingTasks.lateCheckout?.complete;

  const steps: StepInfo[] = [
    {
      title: 'Request late checkout',
      content: ({ goToNextStep }) => (
        <ClosingTaskWizardLayout>
          <RequestLateCheckoutStep
            offer={offer}
            closingTasks={closingTasks}
            goToSummaryForm={goToNextStep}
            mt="10"
            mb="9"
          />
        </ClosingTaskWizardLayout>
      ),
    },
    {
      title: 'Request late checkout',
      hideGoBack: true,
      content: ({ goToNextStep, goToPreviousStep }) => (
        <ClosingTaskWizardLayout>
          <RequestLateCheckoutSummaryStep
            offer={offer}
            closingTasks={closingTasks}
            onClickSignButton={(signer, helloSignUrl) => {
              setContractData({
                signer,
                helloSignUrl,
              });
              goToNextStep();
            }}
            goToEditForm={goToPreviousStep}
            mt="10"
            mb="9"
          />
        </ClosingTaskWizardLayout>
      ),
    },
    {
      title: `Set up and sign ${offer.shouldSeeAgentExperience ? 'the' : 'your'} contract`,
      // eslint-disable-next-line react/display-name
      content: ({ goToPreviousStep }) => {
        if (!closingTasks.signLateCheckoutAddendum) {
          throw new Error('HelloSignStep expects there to be a signLateCheckoutAddendum task');
        }
        if (!contractData) {
          throw new Error('SignPurchaseContract expects a contractData to be set');
        }
        return (
          <HelloSignWizardLayout>
            <HelloSignStep
              contractData={contractData}
              helloSignTaskName="signLateCheckoutAddendum"
              offerId={offer.id}
              onTaskCompletion={(result) => {
                if (result === SignContractResult.success_fully_signed) {
                  history.push(generatePath(getHubSellingRoute(), { addressUUID }));
                } else if (result === SignContractResult.success_not_fully_signed) {
                  goToPreviousStep();
                } else {
                  goToPreviousStep();
                  showToast(
                    'error',
                    'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
                    'cake-late-checkout-sign-contract-error-toast',
                    { toastOptions: { position: 'bottom-left' } },
                  );
                }
              }}
            />
          </HelloSignWizardLayout>
        );
      },
      confirmOnClose: {
        cancelText: 'Finish signing contract',
        confirmText: 'Close without saving',
        content: (
          <Text mb="8">
            You’re almost done signing {offer.shouldSeeAgentExperience ? 'the' : 'your'} contract.
            If you close the window, you’ll have to start over when you return.
          </Text>
        ),
        title: 'Are you sure you want to stop?',
      },
    },
  ];

  return (
    <Wizard
      steps={steps}
      initialStepNumber={
        hasNotSetALateCheckout ? HelloSignWizardStep.FORM_STEP : HelloSignWizardStep.SUMMARY_STEP
      }
      title="Late checkout"
      hideFooter={!!contractData}
      onClose={() =>
        goBack({
          before: initialLocation,
          fallbackLocation: getHubSellingRoute(),
          addressUUID,
        })
      }
    />
  );
};
